// 라우트 + 접근 가드. 카리아카 content.tsx 의 원장/선생님 권한 가드 대신
// "로그인 + 사업장(corp) 보유" 하나만 본다 — 카리페이에는 가맹점 대표 한 종류만 있다.
import React, { useEffect } from "react";
import { Navigate, Route, Routes, matchPath, useLocation } from "react-router-dom";
import { inject, observer } from "mobx-react";

import {
  AuthRoutes,
  CorpExtra,
  CorpMenu,
  HOME_PATH,
  ISubMenu,
  OnboardingRoute,
  PublicRoutes,
} from "@/config/page_route";
import { SERVICE_NAME } from "@/config/const";
import colors from "@/design/colors";
import fonts from "@/design/typography";

const DEFAULT_TITLE = `${SERVICE_NAME} 가맹점 콘솔`;

const CorpRoutes: ISubMenu[] = [...CorpMenu.flatMap((menu) => menu.children), ...CorpExtra];
const AllRoutes: ISubMenu[] = [...AuthRoutes, OnboardingRoute, ...PublicRoutes, ...CorpRoutes];

// 한 화면의 렌더 오류가 콘솔 전체(사이드바 포함)를 하얗게 지우지 않게 화면 단위로 막는다.
// key 를 경로로 주므로 다른 메뉴로 이동하면 다시 그린다.
class PageErrorBoundary extends React.Component<{ children: React.ReactNode }, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error: unknown) {
    console.error("[화면 오류]", error);
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <div style={{ padding: 40, display: "flex", flexDirection: "column", gap: 12 }}>
        <span style={{ ...fonts.title2SemiBold, color: colors.gray900 }}>화면을 불러오지 못했습니다</span>
        <span style={{ ...fonts.body3Regular, color: colors.gray600 }}>
          잠시 후 다시 시도해 주세요. 계속되면 고객센터로 알려 주세요.
        </span>
        <button
          type="button"
          onClick={() => window.location.reload()}
          style={{ ...fonts.body3SemiBold, alignSelf: "flex-start", marginTop: 8, padding: "10px 20px", borderRadius: 10,
            border: `1px solid ${colors.gray200}`, background: colors.white, color: colors.gray900, cursor: "pointer" }}
        >
          새로고침
        </button>
      </div>
    );
  }
}

const Content = ({ rootStore }) => {
  const location = useLocation();
  const isLoggedIn = !!rootStore.token;
  const profile = rootStore.getProfile;
  const hasCorp = profile?.corpId != null;
  const home = hasCorp ? HOME_PATH : OnboardingRoute.path;
  // 로그인 화면은 프로필(member/info)까지 받은 뒤에 넘긴다 — 토큰만 보고 넘기면 사업장 여부를 모른다.
  const afterLogin = hasCorp ? (location.state as any)?.from ?? HOME_PATH : OnboardingRoute.path;

  useEffect(() => {
    const matched = AllRoutes.find((route) => matchPath({ path: route.path, end: true }, location.pathname));
    document.title = matched ? `${matched.title} - ${SERVICE_NAME}` : DEFAULT_TITLE;
  }, [location.pathname]);

  const guarded = (route: ISubMenu) => {
    if (!isLoggedIn) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
    if (!hasCorp) return <Navigate to={OnboardingRoute.path} replace />;
    return <route.component />;
  };

  return (
    <PageErrorBoundary key={location.pathname}>
    <Routes>
      {AuthRoutes.map((route) => (
        <Route
          key={route.path}
          path={route.path}
          element={isLoggedIn && profile ? <Navigate to={afterLogin} replace /> : <route.component />}
        />
      ))}
      {PublicRoutes.map((route) => (
        <Route key={route.path} path={route.path} element={<route.component />} />
      ))}
      <Route
        path={OnboardingRoute.path}
        element={
          !isLoggedIn ? (
            <Navigate to="/login" replace />
          ) : hasCorp ? (
            <Navigate to={HOME_PATH} replace />
          ) : (
            <OnboardingRoute.component />
          )
        }
      />
      {CorpRoutes.map((route) => (
        <Route key={route.path} path={route.path} element={guarded(route)} />
      ))}
      <Route path="*" element={<Navigate to={isLoggedIn ? home : "/login"} replace />} />
    </Routes>
    </PageErrorBoundary>
  );
};

export default inject("rootStore")(observer(Content));
